/**
 * Bucket homework — what goes home tonight, one list per child.
 *
 * It sits under the class action plan because it is the second half of reading
 * one: the sheet says which topic and which children, this says what they each
 * do about it. The rows are named rather than counted, for the same reason the
 * sheet names them — "12 students" sends a teacher back to the matrix to find
 * out which twelve.
 *
 * Nothing here is derived in the component. Every list comes from the child's
 * own action plan on the server, capped at half an hour and put in the order
 * the report tells students to work. What did not fit is counted on the row,
 * so a teacher can see what is waiting rather than wonder.
 *
 * Send sends the list AS THE TEACHER LEFT IT — items dropped, note edited — and
 * it lands on the child's Today and Assigned papers. It sends which items to
 * KEEP, by index, never the items themselves: every link a child is sent was
 * built by the server. The indexes are into the draft this preview was built
 * from, named by `draftedAt`, which no send changes — so "Send again" sends
 * the same list, and a draft regenerated elsewhere is refused, not misread.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';
import { HOMEWORK_CAP_SECONDS, type HomeworkDraft, type HomeworkItem } from '@cwy/config/contracts';

/** The band each item answers — the reason this is "bucket" homework. */
const TYPE_TONE: Record<HomeworkItem['type'], string> = {
  SPEED_DRILL: 'var(--purple-text)',
  PRACTICE: 'var(--blue-text)',
  VIDEO: 'var(--orange-text)',
  SCAFFOLD: 'var(--pink-text)',
  PEER_PAIR: 'var(--blue-text)',
};

const FLAG_LABEL: Record<string, string> = {
  orange_pending_reteach: 'Re-teach before sending',
  pink_needs_teacher: 'Needs you first',
};

const mins = (seconds: number): number => Math.round(seconds / 60);

/** The API's own sentence, when it sent one — a stale draft says what to do next. */
function serverMessage(err: unknown): string | null {
  const message = (err as { response?: { data?: { error?: unknown } } }).response?.data?.error;
  return typeof message === 'string' ? message : null;
}

export function HomeworkPanel({
  previews,
  nameFor,
}: {
  previews: HomeworkDraft[];
  nameFor: (studentId: string) => string;
}) {
  // A local copy, so a teacher can drop an item or reword the note before it
  // goes. Each item carries its index in the generated list, which is what
  // Send names back to the server.
  const [rows, setRows] = useState(() =>
    previews.map((p) => ({ ...p, items: p.items.map((item, at) => ({ ...item, at })) })),
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sent, setSent] = useState<Set<string>>(
    () => new Set(previews.filter((p) => p.sentAt).map((p) => p.homeworkId)),
  );

  const send = useMutation({
    mutationFn: async () => {
      for (const row of rows) {
        await apiPost(`/teacher/homework/${row.homeworkId}/send`, {
          keep: row.items.map((item) => item.at),
          message: row.message,
          draftedAt: row.draftedAt,
        });
        setSent((s) => new Set(s).add(row.homeworkId));
      }
    },
  });

  type Row = (typeof rows)[number];
  function patchRow(homeworkId: string, patch: (r: Row) => Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.homeworkId === homeworkId ? { ...r, ...patch(r) } : r)));
  }

  function removeItem(homeworkId: string, index: number) {
    patchRow(homeworkId, (r) => {
      const items = r.items.filter((_, i) => i !== index);
      return { items, estTotalSeconds: items.reduce((s, it) => s + it.estSeconds, 0) };
    });
  }

  const totalMinutes = mins(rows.reduce((s, r) => s + r.estTotalSeconds, 0));
  const held = rows.reduce((s, r) => s + r.heldBack, 0);

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--paper-sunk)' }}
      >
        <div className="min-w-0">
          <p className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
            Homework preview
          </p>
          <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {rows.length} students · {totalMinutes} min of work in total · up to{' '}
            {mins(HOMEWORK_CAP_SECONDS)} min each
            {held > 0 && ` · ${held} items held for the next set`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => send.mutate()}
          disabled={send.isPending || rows.length === 0}
          className="flex-none rounded-full px-4 py-2 text-xs font-bold text-white transition disabled:opacity-40"
          style={{ background: 'var(--brand-forest)' }}
        >
          {send.isPending ? 'Sending…' : sent.size ? 'Send again' : 'Send to all'}
        </button>
      </div>

      <ul>
        {rows.map((row) => {
          const open = expanded === row.homeworkId;
          return (
            <li key={row.homeworkId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <button
                type="button"
                onClick={() => setExpanded(open ? null : row.homeworkId)}
                aria-expanded={open}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left"
              >
                <span
                  className="min-w-0 flex-1 truncate text-fs-13 font-bold"
                  style={{ color: 'var(--text-strong)' }}
                >
                  {nameFor(row.studentId)}
                </span>

                <span className="flex-none text-xs font-semibold" style={{ color: 'var(--ink-2)' }}>
                  {row.items.length} {row.items.length === 1 ? 'item' : 'items'} ·{' '}
                  {mins(row.estTotalSeconds)} min
                  {row.heldBack > 0 && (
                    <span style={{ color: 'var(--ink-3)' }}> · {row.heldBack} held</span>
                  )}
                </span>

                {row.flags.map((flag) => (
                  <span
                    key={flag}
                    className="flex-none rounded-full px-2 py-0.5 text-fs-10 font-bold uppercase tracking-wide"
                    style={{ background: 'var(--orange-fill)', color: 'var(--orange-deep)' }}
                  >
                    {FLAG_LABEL[flag] ?? flag}
                  </span>
                ))}

                <span
                  className="flex-none text-fs-11 font-bold"
                  style={{ color: sent.has(row.homeworkId) ? 'var(--green-text)' : 'var(--ink-3)' }}
                >
                  {sent.has(row.homeworkId) ? 'Sent' : 'Preview'}
                </span>
              </button>

              {open && (
                <div className="px-4 pb-3" style={{ background: 'var(--paper-sunk)' }}>
                  <label className="block pt-2">
                    <span
                      className="mb-1 block text-fs-10 font-bold uppercase tracking-[0.08em]"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      Note to {nameFor(row.studentId).split(' ')[0]} · drafted for you, yours to
                      change
                    </span>
                    <textarea
                      value={row.message}
                      onChange={(event) =>
                        patchRow(row.homeworkId, () => ({ message: event.target.value }))
                      }
                      rows={3}
                      className="w-full rounded-lg p-2 text-fs-12"
                      style={{
                        background: 'var(--surface-card)',
                        border: '1px solid var(--border-default)',
                        color: 'var(--ink-2)',
                      }}
                    />
                  </label>
                  <ul className="mt-1">
                    {row.items.map((item, index) => (
                      <li
                        key={`${item.type}-${index}`}
                        className="flex items-center justify-between gap-3 py-1"
                      >
                        <span
                          className="text-fs-12 font-semibold"
                          style={{ color: TYPE_TONE[item.type] }}
                        >
                          {item.label}
                          <span className="font-normal" style={{ color: 'var(--ink-3)' }}>
                            {' '}
                            · {mins(item.estSeconds)} min
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(row.homeworkId, index)}
                          className="flex-none text-fs-11 font-bold"
                          style={{ color: 'var(--danger)' }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                    {row.items.length === 0 && (
                      <li className="py-1 text-fs-12 italic" style={{ color: 'var(--ink-3)' }}>
                        Nothing left to send — this paper is all first-attempt solves.
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {send.isError && (
        <p className="px-4 py-2 text-xs" style={{ color: 'var(--danger)' }}>
          {serverMessage(send.error) ?? "Some lists didn't send. Try again."}
        </p>
      )}
    </div>
  );
}
